import Head from 'next/head';
import { request } from 'graphql-request';
import dynamic from 'next/dynamic';
import SiteSettings from '~/siteSettings';
import { get, getStructuredData, getContentPaths, getTaxonomyTerms, getBlocksFromQueue, getAlias, getDrupalSettings } from '~/utils/utils';
import BlockFeatured from '~/components/blockFeatured/blockFeatured';
import BlockParser from '~/components/blockParser/blockParser';
import FeaturedNode from '../../components/featuredNode/featuredNode';
import PageWrapper from '~/components/wrappers/pageWrapper';
import Share from '~/components/share/share';

// GQL
import GET_VERTICAL from '~/graphql/GET_VERTICAL.graphql';
import GET_VERTICAL_CATEGORY_FEATURED_ITEMS from '~/graphql/GET_VERTICAL_CATEGORY_FEATURED_ITEMS.graphql';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });

/**
 * Vertical / Section
 * @category Page
 * @extends Component
 * @summary Display the vertical page.
 * @return {Node} React node containing the vertical page.
 */

const Vertical = props => {
  const { router, sitePrimaryRsid, baseUrl, data, taxonomy, featured, blocks, drupalSettings } = props;
  const blockItems = blocks?.entitySubQueueQuery?.items;
  const path = get(router, 'query.alias')
    ? `/section/${ router.query.alias.join('/') }`
    : '/';

  const content = {
    ...data.nodeQuery
  };

  return (
    <>
      <Head>
        <title>{ content.title } | { SiteSettings.siteName }</title>
        <meta name="title" content={ content.title } />
        <link rel='canonical' href={`${ baseUrl }${ path }`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ content.field_social_headline || content.title } />
        <meta name="description" content={ content.field_seo_description || content.field_subhead } />
        <meta property="og:url" content={ path } />
        <meta property="og:title" content={ content.field_social_headline || content.title } />
        <meta property="og:image" content={ content.field_header_image.field_image.social.url } />
        <meta property="og:description" content={ content.field_social_description || content.field_subhead } />
        <link rel="image_src" href={ content.field_header_image.field_image.social.url } />
        <meta name="twitter:url" content={ path } />
        <meta name="twitter:image:width" content={ content.field_header_image.field_image.social.width } />
        <meta name="twitter:image:height" content={ content.field_header_image.field_image.social.height } />
        <meta name="twitter:image" content={ content.field_header_image.field_image.social.url } />
        <meta property="article:published_time" content={ content.field_display_date } />
        <meta name="keywords" content={ SiteSettings.defaultKeywords } />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, path, content)) }}
        />
        <script key='twitter-script' dangerouslySetInnerHTML={{ __html: `
          window.twttr = (function(d, s, id) {
            var js, fjs = d.getElementsByTagName(s)[0],
              t = window.twttr || {};
            if (d.getElementById(id)) return t;
            js = d.createElement(s);
            js.id = id;
            js.src = "https://platform.twitter.com/widgets.js";
            fjs.parentNode.insertBefore(js, fjs);

            t._e = [];
            t.ready = function(f) {
              t._e.push(f);
            };

            return t;
          }(document, "script", "twitter-wjs"));
        `}} />
      </Head>
      <div className='vertical content'>
        <div className="header--wrapper">
          <picture className='header--image'>
            <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ content.field_header_image.field_image.sm.url } />
            <img
              src={ content.field_header_image.field_image.lg.url }
              alt={ content.field_header_image.field_image.alt }
              className='card__img'
              loading='lazy'
            />
          </picture>
          <Share />
          { content.field_subhead ?
            <h1>{ content.field_subhead }</h1>
          :
            <h1>{ content.title }</h1>
          }
        </div>
        <picture className='secondary--image'>
          <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ content.field_secondary_image.field_image.sm.url } />
          <img
            src={ content.field_secondary_image.field_image.lg.url }
            alt={ content.field_secondary_image.field_image.alt }
            className='card__img'
            loading='lazy'
          />
        </picture>
      </div>
      <div className='spacer spacer--2 wrap-bg-primary'></div>
      { !Array.isArray(featured) || !featured.length &&
        <FeaturedNode
          drupalSettings={ drupalSettings }
          linkToContent
          data={ featured }
        />
      }
      <div className="secondary-content">
        { taxonomy?.viewQuery?.resultRow &&
          <BlockFeatured
            items={ taxonomy.viewQuery.resultRow }
            title='related'
            ajaxTerm={ data.nodeQuery.field_category.tid }
            itemsPerPage={ 4 }
            itemCount={ taxonomy.viewQuery.count }
          />
        }
      </div>
      { blockItems &&
        <BlockParser blockItems={ blockItems } />
      }
      <div data-ad-ref='leader-plus-flex-bottom' className='bidbarrel-ad'></div>
      <div data-ad-ref='mobile-plus-flex-bottom' className='bidbarrel-ad'></div>
      <Utag
        pageType='section_door'
        siteSection={ content?.field_category?.name }
        data={{ sitePrimaryRsid }}
      />
      <BidBarrelReact
        data={ content }
        mapToAdUnits={{
          mobile: [
            'mobile-plus-flex-top',
            'mobile-plus-flex-middle',
            'mobile-plus-flex-bottom'
          ],
          desktop: [
            'leader-plus-flex-top',
            'leader-plus-flex-middle',
            'leader-plus-flex-inc',
            'leader-plus-flex-bottom'
          ]
        }}
      />
    </>
  );
}

export async function getStaticPaths() {
  const paths = await getContentPaths('/section/', {
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'vertical_page'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'vertical_landing_page_blocks',
    limit: 100
  });

  const aliasData = await getAlias({
    alias: `/section/${ ctx.params.alias.join('/') }`
  });

  if(!aliasData?.pathAliasQuery?.nid) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Data
  const data = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_VERTICAL, {
    target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
  });

  if(!data?.nodeQuery) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get related taxonomy nodes
  const taxonomy = await getTaxonomyTerms({
    machine_name: 'latest_content_by_term',
    display: 'master',
    itemsPerPage: 3,
    offset: 0,
    viewArgs: [
      'video,article,gallery,recipe',
      data.nodeQuery.field_category.tid.toString()
    ],
    target_id: parseInt(data.nodeQuery.field_category.tid, 10)
  });

  // Get featured data
  let featured = [];
  if (typeof taxonomy.viewQuery.resultRow[0] !== 'undefined') {
    featured = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_VERTICAL_CATEGORY_FEATURED_ITEMS, {
      target_id: parseInt(taxonomy.viewQuery.resultRow[0].nid, 10)
    });
  }

  // Get Site Settings
  const drupalSettings = await getDrupalSettings();

  return {
    props: {
      blocks,
      data,
      taxonomy,
      featured,
      drupalSettings
    },
    revalidate: 1
  }
}

export default PageWrapper(Vertical);
